import React, {Component, Fragment} from 'react';
import PropTypes from 'prop-types';
import {change, Field, reduxForm} from 'redux-form';
import {connect} from "react-redux";
import {compose} from "recompose";
import {
    LongTextInput,
    SelectInput,
    TextInput
} from 'admin-on-rest';
import {HOURS_24_AMPM, WEEKDAYS} from '../shared/dateTimeLists';
import {addLeadingZero, convertDate, isBlank, validateEmailArray} from '../shared/functions';
import {assembleReportPayload} from '../shared/reportFunctions';
import {getScheduledReportExpirationDate} from '../shared/scheduledReportFunctions';
import RenderReport from './RenderReport';
import ScheduleReportFormDateRangeTypeFields from './ScheduleReportFormDateRangeTypeFields';
import ScheduleReportFormSummary from './ScheduleReportFormSummary';
import SecondaryFilterFieldsWithLocations from './SecondaryFilterFieldsWithLocations';
import {commonStyles} from '../shared/styles';
import {filterStyles} from '../shared/styles-filter';
import RaisedButton from 'material-ui/RaisedButton';
import CircularProgress from 'material-ui/CircularProgress';
import FormattedDateTextInput from './FormattedDateTextInput';

/**
 * A form for scheduling a Report
 *
 */
class ScheduleReportForm extends Component {
    constructor(props) {
        super(props);
        this.state = {
            dateRangeType: null,
            displayDaySelection: false,
            displayForm: true,
            runAtHour: null,
            runAtType: null,
            secondaryFilter: 'secondary-with-locations'
        };
        this.getFormValues = this.getFormValues.bind(this);
        this.handleDateSelect = this.handleDateSelect.bind(this);
        this.handleFieldUpdate = this.handleFieldUpdate.bind(this);
    }

    getFormValues = () => {
        return JSON.parse(JSON.stringify(this.props.formValues));
    }

    handleDateSelect(field, value) {
        if (field && value) {
            let fieldName = null;
            switch (field) {
                case 'end_date':
                    fieldName = 'dateEnd';
                    break;
                case 'expires':
                    fieldName = 'dateExpires';
                    break;
                case 'start_date':
                    fieldName = 'dateStart';
                    break;
                default:
                    break;
            }
            // Convert date selection from YYYY-MM-DDT00:00:00.000Z to yyyy-mm-dd
            let convertedValue = convertDate(value, 'query', false, true, null);
            this.props.dispatch(change('scheduleReportForm', fieldName, convertedValue));
        }
    }

    handleRunAtTimeSelect(field, value) {
        let runAtHour = this.state.runAtHour;
        let runAtTime = this.state.runAtTime;
        if (field && value) {
            if (field === 'runAtHour') {
                runAtHour = value;
            }
            if (!runAtTime && runAtHour) {
                runAtTime = runAtHour + ':00';
                this.props.dispatch(change('scheduleReportForm', 'runAtTime', runAtTime));
            }
            this.setState({
                [field]: value,
                runAtTime: runAtTime
            });
        }
    }

    handleFieldUpdate(field, event, index) {
        let fieldValue = (index) ? index : null;

        // Future Phase: Display form and set Secondary Filter once Report Type is selected

        // Convert dates
        if (field === 'start_date' || field === 'end_date') {
            this.handleDateSelect(field, fieldValue);
        }

        // Display Weekday selection if Report will be run weekly
        if (field === 'runAtType') {
            this.setState({
                displayDaySelection: (fieldValue === 'weekly')
            });
        }
        // Store any specific Locations from Secondary Filter
        if (field === 'filterLocations') {
            this.props.dispatch(change('scheduleReportForm', 'filter_locations', fieldValue));
        }

        // No email validation based on change
        if (field === 'recipients') {
        }
    }

    handleDateRangeTypeFieldUpdate = (field, value) => {
        if (field === 'start_date' || field === 'end_date') {
            this.handleDateSelect(field, value);
        } else {
            if (field === 'dateRangeType') {
                this.setState({
                    dateRangeType: value
                });
            }
            if (field === 'runAtType') {
                this.setState({
                    displayDaySelection: (value === 'weekly')
                });
            }
            this.props.dispatch(change('scheduleReportForm', field, value));
        }
    };

    displaySecondaryFilter = () => {
        if (this.props && this.props.formValues) {
            const val = this.getFormValues();
            if (
                !val || !val.dateRangeType
            ) {
                return false;
            }
            return true;
        }
        return false;
    };

    displayPrintButton = (payload) => {
        if (payload) {
            const dr = payload.dateRange;
            if (
                (dr.daysBack === 0 && !isBlank(dr.fromDay) && !isBlank(dr.toDay) && !isBlank(dr.tz)) ||
                (dr.daysBack > 0 && !isBlank(dr.tz))
            ) {
                return true;
            }
        }
        return false;
    };

    validateForm = () => {
        if (this.props && this.props.formValues) {
            const val = this.getFormValues();
            if (val) {
                const dateRangeType = val.dateRangeType;
                const filterTypes = (val.filter_types) ? val.filter_types : 'all';
                if (
                    !dateRangeType ||
                    (dateRangeType === 'specific' && (!val.dateStart || !val.dateEnd || !val.offset)) ||
                    (dateRangeType === 'relative' && (!val.backNum || val.backNum === '' || val.backNum < 1 || !val.backType || !val.offset)) ||
                    ((filterTypes === 'statecity' || filterTypes === 'statedba') && !val.filter_second_choice) ||
                    (filterTypes !== 'locations' && filterTypes !== 'all' && !val.filter_first_choice) ||
                    (filterTypes === 'locations' && (!val.filter_locations || val.filter_locations.length === 0)) ||
                    !val.name ||
                    !val.runAtTime || !val.runAtType || (val.runAtType === 'weekly' && !val.runAtWeekday) ||
                    !val.recipients || val.recipients === ''
                ) {
                    return false;
                }
                return true;
            }
        }
        return false;
    };

    assemblePayload = (formValues) => {
        let payload = assembleReportPayload();
        payload.dateRange.tz = formValues.offset;
        if (formValues.dateRangeType === 'specific') {
            payload.dateRange.daysBack = 0;
            payload.dateRange.fromDay = formValues.dateStart;
            payload.dateRange.toDay = formValues.dateEnd;
        }
        if (formValues.dateRangeType === 'relative') {
            // Calculate days back based on selection
            let daysBack = formValues.backNum;
            if (formValues.backType === 'weeks') {
                daysBack *= 7;
            }
            if (formValues.backType === 'months') {
                daysBack *= 30;
            }
            payload.dateRange.daysBack = daysBack;
        }
        const filterTypes = formValues.filter_types;
        const filterFirst = formValues.filter_first_choice;
        const filterSecond = formValues.filter_second_choice;
        if (filterTypes) {
            if (filterTypes === 'dba' && filterFirst) {
                payload.location.dbas.push(filterFirst);
            }
            if (filterTypes === 'district' && filterFirst) {
                payload.location.districts.push(filterFirst);
            }
            if (filterTypes === 'location' && filterFirst) {
                payload.location.locations.push(filterFirst.toString());
            }
            if (filterTypes === 'locations') {
                payload.location.locations = formValues.filter_locations;
            }
            if (filterFirst &&
                (filterTypes === 'state' || filterTypes === 'statecity' || filterTypes === 'statedba')
            ) {
                payload.location.states.push(filterFirst);
            }
            if (filterTypes === 'statecity' && filterSecond) {
                payload.location.cities.push(filterSecond);
            }
            if (filterTypes === 'statedba' && filterSecond) {
                payload.location.dbas.push(filterSecond);
            }
        }
        return payload;
    };

    handleSubmit = () => {
        const formValues = this.getFormValues();

        // Convert Recipients to an array
        let recipients = formValues.recipients.split(',');
        for (let r = 0; r < recipients.length; r++) {
            recipients[r] = recipients[r].trim();
        }

        // Assemble data
        const data = {
            created_by: localStorage.getItem('email'),
            emails: recipients,
            expires: (formValues.dateExpires) ? formValues.dateExpires : '',
            frequency: formValues.runAtType,
            name: (formValues.name) ? formValues.name : '',
            payload: this.assemblePayload(formValues),
            report_day: '',
            run_time: formValues.runAtTime
        };
        if (formValues.dateRangeType === 'specific') {
            // Override expiration if previously set
            const exp = getScheduledReportExpirationDate(formValues);
            data.expires = exp.getFullYear().toString() + '-' + addLeadingZero((exp.getMonth() + 1).toString()) + '-' + addLeadingZero(exp.getDate().toString());
        }
        if (formValues.runAtType === 'weekly') {
            data.report_day = formValues.runAtWeekday;
        }

        if (typeof this.props.onFormComplete === 'function') {
            this.props.onFormComplete(data);
        }
    };

    render() {
        const {
            dateRangeType,
            displayDaySelection,
            displayForm,
            secondaryFilter
        } = this.state;

        const {
            formValues,
            submitting,
            success
        } = this.props;

        const payload = (formValues) ? this.assemblePayload(formValues) : null;
        const displayPrintButton = this.displayPrintButton(payload);

        return (
            <div className="list-filter">
                <form>
                    {displayForm &&
                        <Fragment>

                            {/* Date Range Type */}
                            <ScheduleReportFormDateRangeTypeFields
                                formValues={formValues}
                                onFieldUpdate={this.handleDateRangeTypeFieldUpdate}
                            />

                            {/* Secondary Filter */}
                            {secondaryFilter && this.displaySecondaryFilter() === true &&
                                <div>
                                    <br/>
                                    <span>Select an optional secondary filter to narrow results.</span>
                                    <br/>
                                </div>
                            }
                            <SecondaryFilterFieldsWithLocations
                                type="create"
                                displayFields={(secondaryFilter && this.displaySecondaryFilter() === true)}
                                includeLocationSubmit={false}
                                onFilterChoiceSelect={(i) => this.handleFieldUpdate('filterChoice', null, i)}
                                onFilterLocations={(i) => this.handleFieldUpdate('filterLocations', null, i)}
                            />

                            {/* Generate Report */}

                            {displayPrintButton && payload &&
                                <RenderReport
                                    payload={payload}
                                    label="Generate PDF Preview"
                                    buttonType="text"
                                    filename={`Scheduled-Report`}
                                />
                            }

                            {/* Job Frequency */}
                            <hr style={{...commonStyles.divider, ...commonStyles.mt1, ...commonStyles.mb0}}/>

                            <h3>Generate Report At:</h3>
                            <div style={commonStyles.mt2}>
                                Select a frequency and time to run this Report. If Frequency is weekly, select a
                                weekday.
                            </div>
                            <div className="list-filter">
                                <span style={filterStyles.inlineText}>Run Report:</span>
                                <Field
                                    name="runAtType"
                                    component={SelectInput}
                                    label="Frequency"
                                    choices={[
                                        {id: 'weekly', name: 'Weekly'},
                                        {id: 'daily', name: 'Daily'},
                                    ]}
                                    onChange={(e, i, v) => this.handleFieldUpdate('runAtType', e, i, v)}
                                    elStyle={{...filterStyles.inline, ...filterStyles.selectInput, ...filterStyles.selectInputSmall}}
                                />
                                {displayDaySelection &&
                                    <Field
                                        name="runAtWeekday"
                                        component={SelectInput}
                                        label="Weekday"
                                        choices={WEEKDAYS}
                                        elStyle={{...filterStyles.inline, ...filterStyles.selectInput, ...filterStyles.selectInputSmall}}
                                    />
                                }
                                <span style={filterStyles.inlineText}>at:</span>
                                <Field
                                    name="runAtHour"
                                    component={SelectInput}
                                    label="Hour"
                                    choices={HOURS_24_AMPM}
                                    onChange={(e, i) => this.handleRunAtTimeSelect('runAtHour', i)}
                                    elStyle={{...filterStyles.selectInputSmall, ...filterStyles.inline}}
                                />
                            </div>

                            {/* Job Details */}
                            <hr style={{...commonStyles.divider, ...commonStyles.mt1, ...commonStyles.mb0}}/>

                            <h3>Scheduled Report Details</h3>
                            <div className="list-filter">
                                <Field
                                    name="name"
                                    component={TextInput}
                                    label="Schedule Name"
                                    elStyle={filterStyles.inline}
                                />
                                {dateRangeType === 'relative' &&
                                    <FormattedDateTextInput
                                        name="expires"
                                        source="expires"
                                        label="Job Expires:"
                                        onChangeFunc={(e, i, v) => this.handleDateSelect('expires', i)}
                                        minDate={new Date()}
                                    />
                                }
                            </div>

                            {/* Report Recipients */}
                            <br/>
                            <hr style={{...commonStyles.divider, ...commonStyles.mt1, ...commonStyles.mb1}}/>

                            <h3>Recipients:</h3>
                            <p>
                                Enter valid email addresses of all recipients, separated by commas.
                            </p>
                            <div className="list-filter">
                                <Field
                                    name="recipients"
                                    component={LongTextInput}
                                    label="Recipients"
                                    validate={validateEmailArray}
                                    onChange={(e, i, v) => this.handleFieldUpdate('recipients', e, i, v)}
                                />
                            </div>

                            {/* Report Summary */}
                            {this.validateForm() &&
                                <ScheduleReportFormSummary formValues={formValues}/>
                            }

                        </Fragment>
                    }
                    <hr style={filterStyles.break}/>

                    <RaisedButton
                        primary
                        type="button"
                        label="Schedule Report"
                        style={commonStyles.buttonInline}
                        disabled={(this.validateForm() === false || submitting || success)}
                        onClick={() => this.handleSubmit()}
                    />
                    <RaisedButton
                        type="button"
                        label="Cancel"
                        href="/#/ScheduledReports"
                        disabled={(submitting || success)}
                        style={commonStyles.buttonInline}
                    />
                    <div className="loader-container-left" style={commonStyles.ml0}>
                        {submitting &&
                            <div>
                                <h4>Saving Scheduled Report...</h4>
                                <CircularProgress
                                    size={24}
                                    thickness={2}
                                    className="loader"
                                />
                            </div>
                        }
                        {success &&
                            <div>
                                <h4>Scheduled Report saved</h4>
                                <RaisedButton
                                    primary
                                    type="button"
                                    label="View Reports"
                                    href="/#/ScheduledReports"
                                    style={commonStyles.buttonInline}
                                />
                            </div>
                        }
                    </div>
                </form>
                <hr style={filterStyles.break}/>
            </div>
        );
    }
}

const mapStateToProps = (state) => {
    let form = state.form['scheduleReportForm'];
    if (form && form.values) {
        return {
            formValues: form.values
        }
    }
    return {}
};

ScheduleReportForm.propTypes = {
    onFormComplete: PropTypes.func,
    submitting: PropTypes.bool,
    success: PropTypes.bool
};

ScheduleReportForm.defaultProps = {
    submitting: false,
    success: false
};

const enhance = compose(
    connect(mapStateToProps),
    reduxForm({
        form: 'scheduleReportForm',
        enableReinitialize: true,
    })
);

export default enhance(ScheduleReportForm);



// WEBPACK FOOTER //
// ./src/Fuse/components/ScheduleReportForm.js